import { css, cx } from "@emotion/css";
import { borderRadius, fontFamily, themeTokens } from "@octopusdeploy/design-system-tokens";
import React from "react";
import { useCanLoadImage } from "./utils";
export type AvatarSize = "large" | "medium" | "small";
/**
 * Wraps the avatar in a clickable link or a static container.
 * Displays an "edit" overlay if `hasEditOverlay` is true.
 *
 * @param {object} props - The component props.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {string} props.className - The CSS class for styling the wrapper.
 * @param {React.ReactNode} props.children - The avatar content.
 * @param {boolean} [props.hasEditOverlay] - Whether to show the "edit" overlay.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} The wrapper element.
 */
function AvatarWrapper({ href, className, children, hasEditOverlay, title }: {
    href?: string;
    className: string;
    children: React.ReactNode;
    hasEditOverlay?: boolean;
    title?: string;
}) {
    if (href) {
        return (<a href={href} className={className} title={title}>
                {children}
                {hasEditOverlay && <p className={cx("avatarWrapperEditOverlay", avatarEditStyles)}>Edit</p>}
            </a>);
    }
    return (<div className={className} title={title}>
            {children}
        </div>);
}
export interface AvatarProps {
    className?: string;
    size: AvatarSize;
    src?: string;
    srcSet?: string;
    alt?: string;
    href?: string;
    hasEditOverlay?: boolean;
    isMuted?: boolean;
    fallback: React.ReactNode;
    shape: AvatarShape;
    title?: string;
}
export type AvatarShape = "circle" | "rounded";
/**
 * The base component for rendering an avatar.
 * Handles image loading, fallback rendering, and wrapper behavior.
 *
 * @param {string} [props.className] - Additional CSS styles to apply to the avatar.
 * @param {object} props - The component props.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to show the "edit" overlay.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {React.ReactNode} props.fallback - The fallback content if the image fails to load.
 * @param {AvatarType} props.type - The type of the avatar (user, space, project, etc.).
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} The rendered avatar.
 */
export function Avatar({ className, shape, size, src, srcSet, alt, href, hasEditOverlay, isMuted, fallback, title }: AvatarProps): React.ReactElement {
    const sizeStyles = getAvatarShapeStyles(shape, size);
    const canLoadImage = useCanLoadImage(src || "");
    const optionalImageProps = {
        ...(srcSet && { srcSet }),
    };
    return (<AvatarWrapper href={href} className={cx(rootAvatarStyles, sizeStyles, className, { [isMutedAvatarStyles]: isMuted })} hasEditOverlay={hasEditOverlay} title={title}>
            {src && canLoadImage === true ? <img className={avatarImageStyles} src={src} {...optionalImageProps} alt={alt}/> : fallback}
        </AvatarWrapper>);
}
// Shared avatar styles
const rootAvatarStyles = css({
    display: "flex",
    flexShrink: 0,
    userSelect: "none",
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    containerType: "inline-size",
    textDecoration: "none",
    color: themeTokens.color.avatar.text,
    fontFamily: fontFamily.default,
    position: "relative",
    transition: "background-color 150ms ease-in-out",
    backgroundColor: themeTokens.color.background.secondary.default,
    // Note: Have to set the color for hover and focus because of the global LESS files for text colors
    // If we dont set it will inherit from the LESS styles instead of the component
    "&[href]:hover": {
        color: themeTokens.color.avatar.text,
    },
    "&[href]:focus-visible": {
        color: themeTokens.color.avatar.text,
        outline: `2px solid ${themeTokens.color.border.selected}`,
        outlineOffset: "2px",
    },
    "&[href]:hover, &[href]:focus-visible": {
        backgroundColor: themeTokens.color.background.tertiary,
    },
    // Display Edit text on hover or focus
    "&[href]:hover > .avatarWrapperEditOverlay, &[href]:focus-visible > .avatarWrapperEditOverlay": {
        opacity: "1",
        transition: "opacity 150ms ease-in-out",
    },
    // Different background colour for images so if the image is transparent the background is less intrusive
    "& img": {
        backgroundColor: themeTokens.color.avatar.background.image.default,
        transition: "background-color 150ms ease-in-out",
    },
    "&[href]:hover img": {
        backgroundColor: themeTokens.color.avatar.background.image.hover,
    },
});
// State Styles
const isMutedAvatarStyles = css({
    opacity: 0.4,
});
const avatarEditStyles = css({
    color: themeTokens.color.avatar.text,
    position: "absolute",
    inset: 0,
    display: "grid",
    placeContent: "center",
    overflow: "hidden",
    backgroundColor: themeTokens.color.overlay.primary,
    fontSize: "34cqi",
    containerType: "inline-size",
    margin: 0,
    opacity: 0,
});
// Variant styles
const avatarInitialsFallback = css({
    fontSize: "44cqi",
    display: "block",
    margin: 0,
    color: themeTokens.color.avatar.text,
});
const avatarImageStyles = css({
    objectFit: "scale-down",
    width: "100%",
    height: "100%",
    aspectRatio: "1 / 1",
});
// Maps avatar sizes to their pixel dimensions.
export const avatarSizeMap = {
    small: 20,
    medium: 36,
    large: 48,
};
const shapeStyleMap = {
    rounded: css({ borderRadius: borderRadius.small }),
    circle: css({ borderRadius: borderRadius.circle }),
} as const;
function getAvatarShapeStyles(shape: AvatarShape, size: AvatarSize) {
    const heightStyles = css({
        height: `${avatarSizeMap[size]}px`,
        width: `${avatarSizeMap[size]}px`,
    });
    const shapeStyles = shapeStyleMap[shape];
    return cx(heightStyles, shapeStyles);
}
